/**
 * VaultDeckEditor — editing the words on a PowerPoint deck's slides.
 *
 * A deck's value is its design, and no editor of ours should try to rebuild
 * that. What people actually need to change is the text: a stale figure in a
 * bullet, a title before sending it on. So edits land back in the original
 * shapes, and every layout, master, image, chart and animation in the file is
 * left alone.
 *
 * Editing happens ON the slide. The surface is the preview's own — the same
 * 16:9 sheets, the same type scale, the same line picked as the title (see
 * `deckTitlePos`) — so switching to Edit does not switch you to a different,
 * boxier-looking deck. The differences are only the ones editing requires: the
 * sheet may grow past 16:9 rather than clip what you are typing, and the
 * paragraphs take focus.
 *
 * Runs carry bold / italic / underline / colour, the same model the Word
 * editor uses, so formatting inside a bullet survives a round trip.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { deckTitlePos } from '@/components/common/SlideDeckView';
import '@/components/common/SlideDeckView.css';
import { Run, htmlToRuns, runsToHtml } from './docModel';

const COLOURS = ['000000', 'FFFFFF', 'C00000', 'B45F06', 'BF9000', '38761D', '1155CC', '674EA7'];

interface DeckPara { runs: Run[]; lvl?: number; bu?: boolean }
interface DeckShape { id: number; name: string; kind?: string | null; paras: DeckPara[] }
interface DeckSlide { n: number; layout?: string; shapes: DeckShape[] }
interface Deck { slides: DeckSlide[] }

export function parseDeck(value: string): Deck | null {
  if (!value || !value.trimStart().startsWith('{')) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || !Array.isArray(parsed.slides)) return null;
    return { slides: parsed.slides };
  } catch {
    return null;
  }
}

const serialiseDeck = (deck: Deck) => JSON.stringify({ vault_deck: 1, slides: deck.slides });

/** Does this line draw a bullet? The FILE decides, not us.
 *
 *  `bu` is carried in the envelope straight from the paragraph's own PowerPoint
 *  properties (buChar / buAutoNum / buNone, else the placeholder's inherited
 *  default). The viewer used to infer it as "anything that is not the title",
 *  which drew a dot in front of the deck's own title block and its footer —
 *  marks PowerPoint never renders (seen 2026-09-04). Absent on an envelope
 *  built before this shipped, and absent means no bullet: a missing dot reads
 *  as a style choice, a spurious one reads as a bug. */
const paraIsBulleted = (para: DeckPara) => para.bu === true;

/** An empty paragraph never takes a bullet — a lone dot on a blank line reads
 *  as a rendering fault. It stays an editable block, it just loses the marker. */
const paraHasText = (para: DeckPara) =>
  (para.runs || []).some((r) => (r.t || '').trim() !== '');

interface Props {
  value: string;
  readOnly?: boolean;
  onChange: (next: string) => void;
}

export function VaultDeckEditor({ value, readOnly, onChange }: Props) {
  const { t } = useLanguage();
  const [deck, setDeck] = useState<Deck | null>(() => parseDeck(value));
  const [focus, setFocus] = useState<{ s: number; sh: number; p: number } | null>(null);
  const [picker, setPicker] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const lastPushed = useRef(value);

  useEffect(() => {
    if (value !== lastPushed.current) {
      setDeck(parseDeck(value));
      lastPushed.current = value;
    }
  }, [value]);

  const push = useCallback((next: Deck) => {
    setDeck(next);
    const text = serialiseDeck(next);
    lastPushed.current = text;
    onChange(text);
  }, [onChange]);

  if (!deck) return null;

  const key = (s: number, sh: number, p: number) => `${s}-${sh}-${p}`;
  const blockAt = (s: number, sh: number, p: number) =>
    rootRef.current?.querySelector<HTMLDivElement>(`[data-k="${key(s, sh, p)}"]`) ?? null;

  const writeParas = (s: number, sh: number, mutate: (paras: DeckPara[]) => DeckPara[]) =>
    push({
      slides: deck.slides.map((slide, si) =>
        si !== s ? slide : {
          ...slide,
          shapes: slide.shapes.map((shape, hi) =>
            hi !== sh ? shape : { ...shape, paras: mutate(shape.paras) }),
        }),
    });

  const syncBlock = (s: number, sh: number, p: number) => {
    const el = blockAt(s, sh, p);
    if (!el) return;
    const runs = htmlToRuns(el);
    writeParas(s, sh, (paras) => paras.map((para, pi) => (pi === p ? { ...para, runs } : para)));
  };

  const applyCommand = (command: string, argument?: string) => {
    if (!focus) return;
    const el = blockAt(focus.s, focus.sh, focus.p);
    if (!el) return;
    el.focus();
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(command, false, argument);
    syncBlock(focus.s, focus.sh, focus.p);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, s: number, sh: number, p: number) => {
    if (readOnly) return;
    const el = e.currentTarget;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const tail = selection.getRangeAt(0).cloneRange();
      tail.setEndAfter(el.lastChild ?? el);
      const holder = document.createElement('div');
      holder.appendChild(tail.extractContents());
      const before = htmlToRuns(el);
      const after = htmlToRuns(holder);
      writeParas(s, sh, (paras) => {
        const next = paras.slice();
        next.splice(p, 1, { ...paras[p], runs: before }, { runs: after, lvl: paras[p].lvl ?? 0 });
        return next;
      });
      requestAnimationFrame(() => blockAt(s, sh, p + 1)?.focus());
    } else if (e.key === 'Backspace' && p > 0 && (el.textContent ?? '') === '') {
      e.preventDefault();
      writeParas(s, sh, (paras) => paras.filter((_, pi) => pi !== p));
      requestAnimationFrame(() => blockAt(s, sh, p - 1)?.focus());
    }
  };

  const tool = (label: React.ReactNode, title: string, command: string, argument?: string) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      className="vault-tool"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => applyCommand(command, argument)}
    >
      {label}
    </button>
  );

  return (
    <div className="vault-deck" ref={rootRef}>
      {!readOnly && (
        <div className="vault-doc-toolbar">
          <span className="vault-tool-group">
            {tool(<b>B</b>, t('vault.viewer.bold'), 'bold')}
            {tool(<i>I</i>, t('vault.viewer.italic'), 'italic')}
            {tool(<u>U</u>, t('vault.viewer.underline'), 'underline')}
          </span>
          <Popover
            trigger="click"
            placement="bottom"
            title={t('vault.viewer.textColour')}
            open={picker}
            onOpenChange={setPicker}
            content={
              <div className="vault-swatches">
                {COLOURS.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    className="vault-swatch"
                    style={{ background: `#${hex}` }}
                    aria-label={`#${hex}`}
                    onClick={() => { setPicker(false); applyCommand('foreColor', `#${hex}`); }}
                  />
                ))}
              </div>
            }
          >
            <button
              type="button"
              className="vault-tool swatchy"
              title={t('vault.viewer.textColour')}
              aria-label={t('vault.viewer.textColour')}
            >
              A<i style={{ background: '#C00000' }} />
            </button>
          </Popover>
          <span className="vault-deck-hint">{t('vault.viewer.deckHint')}</span>
        </div>
      )}

      {deck.slides.map((slide, s) => {
        const tpos = deckTitlePos(slide);
        const isTitlePara = (sh: number, p: number) => !!tpos && tpos.sh === sh && tpos.p === p;
        // A sparse slide is centred as a title card — the preview's rule, run
        // here too, because a slide that centres in one tab and not the other
        // is not the same slide.
        const bodyLines = slide.shapes.reduce(
          (n, shape, sh) => n + shape.paras.filter(
            (para, p) => paraHasText(para) && !isTitlePara(sh, p)).length,
          0);
        const layout = /title|section/.test((slide.layout || '').toLowerCase()) || bodyLines <= 3
          ? 'title' : 'content';
        return (
          <section className={`artifact-slide artifact-slide--${layout} vault-slide`} key={slide.n}>
            <div className="artifact-slide__num">{slide.n}</div>
            <div className="artifact-slide__inner vault-slide-body">
              {slide.shapes.map((shape, sh) => shape.paras.map((para, p) => (
                <DeckBlock
                  key={key(s, sh, p)}
                  id={key(s, sh, p)}
                  runs={para.runs}
                  level={para.lvl ?? 0}
                  isTitle={isTitlePara(sh, p)}
                  bulleted={paraIsBulleted(para) && paraHasText(para)}
                  readOnly={readOnly}
                  onFocus={() => setFocus({ s, sh, p })}
                  onInput={() => syncBlock(s, sh, p)}
                  onKeyDown={(e) => onKeyDown(e, s, sh, p)}
                />
              )))}
              {slide.shapes.length === 0 && (
                <div className="vault-slide-empty">{t('vault.viewer.slideNoText')}</div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function DeckBlock({
  id, runs, level, isTitle, bulleted, readOnly, onFocus, onInput, onKeyDown,
}: {
  id: string;
  runs: Run[];
  level: number;
  isTitle: boolean;
  bulleted: boolean;
  readOnly?: boolean;
  onFocus: () => void;
  onInput: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const html = runsToHtml(runs);

  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [html]);

  return (
    <div
      ref={ref}
      data-k={id}
      dir="auto"
      role="textbox"
      tabIndex={0}
      className={`vault-slide-para ${isTitle ? 'title' : ''} ${bulleted ? 'bullet' : ''}`}
      // In em, not px: the sheet's type scales with its width, so a fixed
      // indent drifted away from the marker at every size but one.
      style={{ paddingInlineStart: bulleted ? `${1.1 + level * 1.2}em` : undefined }}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      onFocus={onFocus}
      onInput={onInput}
      onBlur={onInput}
      onKeyDown={onKeyDown}
    />
  );
}

export default VaultDeckEditor;
